/**
 * IFRAP-AI.fr - OSINT Module Page (Admin)
 * Source management and monitoring dashboard
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  nouvelle: '#888888',
  candidate: '#9370DB',
  rejetee: '#FF6666',
  active: '#33FF00',
  ok: '#33FF00',
  instable: '#FFB000',
  a_reparer: '#FF6600',
  panne: '#FF3333',
  reparee: '#00BFFF',
  suspendue: '#666666',
  obsolete: '#333333',
};

const STATUS_LABELS = {
  nouvelle: 'Nouvelle',
  candidate: 'Candidate',
  rejetee: 'Rejetée',
  active: 'Active',
  ok: 'OK',
  instable: 'Instable',
  a_reparer: 'À réparer',
  panne: 'Panne',
  reparee: 'Réparée',
  suspendue: 'Suspendue',
  obsolete: 'Obsolète',
};

const OSINTPage = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceDetail, setSourceDetail] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const token = localStorage.getItem('ifrap_token');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, srcRes] = await Promise.all([
        fetch(`${API}/osint/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API}/osint/sources`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);
      
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (srcRes.ok) {
        const data = await srcRes.json();
        setSources(data.sources);
      }
    } catch (err) {
      console.error('Failed to fetch OSINT data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSourceDetail = async (sourceId) => {
    try {
      const response = await fetch(`${API}/osint/sources/${sourceId}?include_health_logs=true`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setSourceDetail(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch source detail:', err);
    }
  };

  const handleSourceClick = (source) => {
    setSelectedSource(source.id);
    fetchSourceDetail(source.id);
  };

  const handleAction = async (sourceId, action, data = {}) => {
    setActionLoading(action);
    try {
      let url = `${API}/osint/sources/${sourceId}/${action}`;
      let method = 'POST';
      let body = JSON.stringify(data);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body,
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(result.message || 'Action effectuée');
        fetchData();
        if (selectedSource) fetchSourceDetail(selectedSource);
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail}`);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddSource = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    
    const data = {
      name: formData.get('name'),
      url: formData.get('url'),
      source_type: formData.get('source_type'),
      description: formData.get('description'),
      category: formData.get('category'),
    };

    try {
      const response = await fetch(`${API}/osint/sources`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        alert('Source créée - en attente de validation');
        setShowAddForm(false);
        form.reset();
        fetchData();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail}`);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 font-mono">Chargement module OSINT...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="osint-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-mono text-[#FFB000]">Module OSINT</h1>
          <p className="text-gray-500 text-sm">Surveillance et gestion des sources</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-[#33FF00] text-black px-4 py-2 rounded font-medium text-sm"
          data-testid="add-source-btn"
        >
          + Ajouter source
        </button>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
            <div className="text-gray-500 text-xs">Total sources</div>
            <div className="text-2xl font-mono text-white">{dashboard.total_sources}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
            <div className="text-gray-500 text-xs">Taux santé</div>
            <div className="text-2xl font-mono text-[#33FF00]">{dashboard.health_rate}%</div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
            <div className="text-gray-500 text-xs">À valider</div>
            <div className="text-2xl font-mono text-[#9370DB]">{dashboard.pending_validation?.length || 0}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
            <div className="text-gray-500 text-xs">Critiques</div>
            <div className="text-2xl font-mono text-[#FF3333]">{dashboard.critical_sources?.length || 0}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
            <div className="text-gray-500 text-xs">Items collectés</div>
            <div className="text-2xl font-mono text-white">{dashboard.total_items_collected}</div>
          </div>
        </div>
      )}

      {/* Pending Validation Alert */}
      {dashboard?.pending_validation?.length > 0 && (
        <div className="bg-[#9370DB]/10 border border-[#9370DB]/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-[#9370DB] font-mono text-sm">VALIDATION REQUISE</span>
          </div>
          <div className="space-y-2">
            {dashboard.pending_validation.map((src) => (
              <div key={src.id} className="flex items-center justify-between">
                <span className="text-white">{src.name}</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAction(src.id, 'validate', { approved: true })}
                    disabled={actionLoading === 'validate'}
                    className="text-xs bg-[#33FF00]/20 text-[#33FF00] px-3 py-1 rounded"
                  >
                    Valider
                  </button>
                  <button
                    onClick={() => handleAction(src.id, 'validate', { approved: false, reason: 'Non pertinent' })}
                    disabled={actionLoading === 'validate'}
                    className="text-xs bg-[#FF3333]/20 text-[#FF3333] px-3 py-1 rounded"
                  >
                    Rejeter
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sources List */}
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg">
        <div className="p-4 border-b border-[#1a1a1a]">
          <h2 className="text-lg font-mono text-white">Sources ({sources.length})</h2>
        </div>
        <div className="divide-y divide-[#1a1a1a]">
          {sources.map((source) => (
            <div
              key={source.id}
              onClick={() => handleSourceClick(source)}
              className={`p-4 cursor-pointer hover:bg-[#1a1a1a]/50 transition-colors ${
                selectedSource === source.id ? 'bg-[#1a1a1a]' : ''
              }`}
              data-testid={`source-${source.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span
                    className="px-2 py-1 rounded text-xs font-mono"
                    style={{
                      backgroundColor: `${STATUS_COLORS[source.status]}20`,
                      color: STATUS_COLORS[source.status],
                    }}
                  >
                    {STATUS_LABELS[source.status] || source.status}
                  </span>
                  <span className="text-white">{source.name}</span>
                  <span className="text-gray-500 text-sm">{source.source_type}</span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>Confiance: {Math.round(source.confidence_score * 100)}%</span>
                  <span>{source.items_collected_total} items</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Source Detail Modal */}
      {selectedSource && sourceDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedSource(null)}>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-mono text-[#FFB000]">{sourceDetail.name}</h3>
                <p className="text-gray-500 text-sm mt-1">{sourceDetail.description}</p>
              </div>
              <button onClick={() => setSelectedSource(null)} className="text-gray-500 hover:text-white">✕</button>
            </div>

            {/* Status & Actions */}
            <div className="flex items-center space-x-4 mb-4">
              <span
                className="px-3 py-1 rounded text-sm font-mono"
                style={{
                  backgroundColor: `${STATUS_COLORS[sourceDetail.status]}20`,
                  color: STATUS_COLORS[sourceDetail.status],
                }}
              >
                {STATUS_LABELS[sourceDetail.status]}
              </span>
              
              {sourceDetail.status === 'candidate' && (
                <>
                  <button
                    onClick={() => handleAction(sourceDetail.id, 'validate', { approved: true })}
                    className="text-sm bg-[#33FF00]/20 text-[#33FF00] px-3 py-1 rounded"
                  >
                    Valider
                  </button>
                  <button
                    onClick={() => handleAction(sourceDetail.id, 'validate', { approved: false, reason: 'Non pertinent' })}
                    className="text-sm bg-[#FF3333]/20 text-[#FF3333] px-3 py-1 rounded"
                  >
                    Rejeter
                  </button>
                </>
              )}
              
              {['active', 'ok', 'instable', 'reparee'].includes(sourceDetail.status) && (
                <button
                  onClick={() => handleAction(sourceDetail.id, 'check')}
                  disabled={actionLoading === 'check'}
                  className="text-sm bg-[#00FFFF]/20 text-[#00FFFF] px-3 py-1 rounded"
                >
                  Tester
                </button>
              )}
              
              {sourceDetail.status === 'a_reparer' && (
                <button
                  onClick={() => handleAction(sourceDetail.id, 'repair')}
                  disabled={actionLoading === 'repair'}
                  className="text-sm bg-[#FFB000]/20 text-[#FFB000] px-3 py-1 rounded"
                >
                  Réparer
                </button>
              )}
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-[#1a1a1a] rounded p-3">
                <div className="text-xs text-gray-500">URL</div>
                <div className="text-sm text-white truncate">{sourceDetail.url}</div>
              </div>
              <div className="bg-[#1a1a1a] rounded p-3">
                <div className="text-xs text-gray-500">Type</div>
                <div className="text-sm text-white">{sourceDetail.source_type}</div>
              </div>
              <div className="bg-[#1a1a1a] rounded p-3">
                <div className="text-xs text-gray-500">Confiance</div>
                <div className="text-sm text-white">{Math.round(sourceDetail.confidence_score * 100)}%</div>
              </div>
              <div className="bg-[#1a1a1a] rounded p-3">
                <div className="text-xs text-gray-500">Échecs consécutifs</div>
                <div className="text-sm text-white">{sourceDetail.consecutive_failures}</div>
              </div>
            </div>

            {/* Health Logs */}
            {sourceDetail.health_logs?.length > 0 && (
              <div>
                <h4 className="text-sm text-gray-400 mb-2">Journal de santé</h4>
                <div className="bg-[#1a1a1a] rounded p-2 max-h-40 overflow-auto font-mono text-xs">
                  {sourceDetail.health_logs.map((log, idx) => (
                    <div key={idx} className="flex items-center space-x-3 py-1">
                      <span className="text-gray-600">{log.checked_at.split('T')[1]?.slice(0, 8)}</span>
                      <span className={log.status === 'success' ? 'text-[#33FF00]' : 'text-[#FF3333]'}>
                        {log.status}
                      </span>
                      <span className="text-gray-500">{log.response_time_ms}ms</span>
                      {log.is_repair_attempt && <span className="text-[#FFB000]">[REPAIR]</span>}
                      {log.error_message && <span className="text-[#FF6666]">{log.error_message}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Source Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddForm(false)}>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-mono text-[#FFB000] mb-4">Nouvelle source</h3>
            <form onSubmit={handleAddSource} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nom</label>
                <input name="name" required className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">URL</label>
                <input name="url" type="url" required className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select name="source_type" className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white">
                  <option value="rss">RSS</option>
                  <option value="api">API</option>
                  <option value="scraper">Scraper</option>
                  <option value="manual">Manuel</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Catégorie</label>
                <input name="category" defaultValue="general" className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea name="description" rows={2} className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white" />
              </div>
              <div className="flex space-x-3">
                <button type="submit" className="flex-1 bg-[#33FF00] text-black py-2 rounded font-medium">Créer</button>
                <button type="button" onClick={() => setShowAddForm(false)} className="flex-1 bg-[#1a1a1a] text-gray-400 py-2 rounded">Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OSINTPage;
